import { FC, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { useFormikContext } from 'formik';
import get from 'lodash/get';
import set from 'lodash/set';

import { UserAddressData } from '@shared/interfaces/UserAddressData';
import CountrySelect from '@shared/components/CountrySelect/CountrySelect';
import { UserAddressFormFields } from '../EditUserAddressComponent/constants/userAddressFormFields';
import { AddressAutocompleteTextField } from '../EditUserAddressComponent/components/AddressAutocompleteTextField';

export interface PhysicalAddressFieldsValues {
  addressLine1: string;
  addressLine2: string;
  city: string;
  zipCode: string;
  state: string;
  country: string;
  countryCode?: string;
}

interface PhysicalAddressFieldsProps {
  fieldPrefix?: string;
}

const FIELDS = UserAddressFormFields;

export const PhysicalAddressFields: FC<PhysicalAddressFieldsProps> = ({ fieldPrefix }) => {
  const { values, handleChange, setValues } = useFormikContext<Record<string, unknown>>();

  const path = (field: string) => (fieldPrefix ? `${fieldPrefix}.${field}` : field);

  const fieldValue = (field: string): string => (get(values, path(field)) as string) ?? '';

  const setValuesForAutofillFields = useCallback(
    (value: Required<UserAddressData>, fieldName: string) => {
      const updated = { ...values };

      const pick = (key: keyof UserAddressData) =>
        fieldName === key ? value[key] || fieldValue(key) : fieldValue(key);

      set(updated, path('addressLine1'), pick('addressLine1'));
      set(updated, path('addressLine2'), pick('addressLine2'));
      set(updated, path('city'), pick('city'));
      set(updated, path('state'), pick('state'));
      set(updated, path('zipCode'), pick('zipCode'));

      setValues(updated);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, setValues, fieldPrefix]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <AddressAutocompleteTextField
          type="text"
          variant="outlined"
          name={path(FIELDS.addressLine1.name)}
          label={FIELDS.addressLine1.label}
          value={fieldValue(FIELDS.addressLine1.name)}
          setValuesForAutofillFields={setValuesForAutofillFields}
          fieldName={FIELDS.addressLine1.name}
          placeholder='Street number, street name, e.g. "123 Main St."'
          autoComplete="shipping address-line1 street-address"
          fullWidth
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          variant="outlined"
          name={path(FIELDS.addressLine2.name)}
          label={FIELDS.addressLine2.label}
          value={fieldValue(FIELDS.addressLine2.name)}
          onChange={handleChange}
          inputProps={{ maxLength: 100 }}
          placeholder="Apartment, suite, unit number, etc."
          autoComplete="shipping address-line2"
          fullWidth
        />
      </Grid>

      <Grid item xs={12} sm={8}>
        <AddressAutocompleteTextField
          variant="outlined"
          name={path(FIELDS.city.name)}
          label={FIELDS.city.label}
          value={fieldValue(FIELDS.city.name)}
          setValuesForAutofillFields={setValuesForAutofillFields}
          fieldName={FIELDS.city.name}
          autoComplete="shipping address-level2"
          fullWidth
          required
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          variant="outlined"
          name={path(FIELDS.zipCode.name)}
          label={FIELDS.zipCode.label}
          value={fieldValue(FIELDS.zipCode.name)}
          onChange={handleChange}
          inputProps={{ maxLength: 10 }}
          fullWidth
          required
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <AddressAutocompleteTextField
          variant="outlined"
          name={path(FIELDS.state.name)}
          label={FIELDS.state.label}
          value={fieldValue(FIELDS.state.name)}
          setValuesForAutofillFields={setValuesForAutofillFields}
          fieldName={FIELDS.state.name}
          placeholder="State/Province/Region"
          fullWidth
          required
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <CountrySelect
          value={fieldValue(FIELDS.country.name)}
          label={FIELDS.country.label}
          onChange={(_, option) => {
            const updated = { ...values };
            set(updated, path(FIELDS.country.name), option?.label ?? '');
            set(updated, path('countryCode'), option?.code ?? '');
            setValues(updated);
          }}
        />
      </Grid>
    </Grid>
  );
};
